import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, inject } from '@angular/core';
import { finalize } from 'rxjs';
import { AuditLogApiService, AuditLogRow } from 'src/app/core/services/audit-log-api.service';
import { AuditEntryDrawerComponent } from 'src/app/pages/audit-log/audit-entry-drawer.component';
import { AuditLogTableComponent } from 'src/app/pages/audit-log/audit-log-table.component';

/** Per-entity change history: who changed this record, when, from where. Drop into any detail page. */
@Component({
  selector: 'app-audit-history',
  standalone: true,
  imports: [CommonModule, AuditEntryDrawerComponent, AuditLogTableComponent],
  templateUrl: './audit-history.component.html',
})
export class AuditHistoryComponent implements OnInit, OnChanges {
  private readonly api = inject(AuditLogApiService);
  @Input({ required: true }) resourceType = '';
  @Input({ required: true }) resourceId: number | string | null = null;

  rows: AuditLogRow[] = [];
  total = 0;
  page = 1;
  readonly pageSize = 20;
  loading = false;
  selected: AuditLogRow | null = null;

  // ngOnInit covers tests/first render where inputs are set directly rather than via a template
  // binding (ngOnChanges never fires in that case); ngOnChanges covers later input changes.
  private initialized = false;

  ngOnInit(): void {
    this.initialized = true;
    if (this.resourceId === null || this.resourceId === '') return;
    this.load();
  }

  ngOnChanges(): void {
    if (!this.initialized) return;
    if (this.resourceId === null || this.resourceId === '') return;
    this.page = 1;
    this.rows = [];
    this.load();
  }

  load(): void {
    this.loading = true;
    // Stamp the request with the resource/page it was issued for: if resourceId changes while
    // this is in flight, a stale response must not append onto the new resource's rows.
    const requestResourceId = this.resourceId;
    const requestPage = this.page;
    this.api
      .list({
        resource_type: this.resourceType,
        resource_id: String(this.resourceId),
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          if (this.resourceId !== requestResourceId || this.page !== requestPage) return;
          this.rows = [...this.rows, ...(res?.data?.result ?? [])];
          this.total = res?.data?.total ?? 0;
        },
        error: () => undefined,
      });
  }

  loadMore(): void {
    this.page += 1;
    this.load();
  }
}
